// Identify Cloud Landscape rows that host the C2 Privacy Platform itself (or a Kubernetes
// cluster it runs on). Such rows keep everything else — visible, expandable, linkable — but
// the lifecycle toggle is disabled: stopping the platform's own nodes from inside the
// platform is never what the operator meant.
//
// Three signals, strongest first. A row matches when any one does.
//
//  1. `c2pp:cluster` tag — stamped on every resource by terraform-aws-c2pp and
//     terraform-azurerm-c2pp (`local.tags`), including EKS managed-node-group instances.
//  2. Kubernetes-node tags the cloud providers add automatically (EKS: `eks:cluster-name`,
//     `kubernetes.io/cluster/<name>`; AKS: `aks-managed-*`). Catches hand-built clusters
//     that skipped the Terraform modules. Broader than "the platform" — but a k8s node's
//     lifecycle belongs to the cluster autoscaler either way, so the toggle is wrong on it.
//  3. Product name hints on the resource name / identifier / `Name` tag, for platforms
//     installed by hand onto a plain VM (docker-compose / swarm) with no tags at all.

const PLATFORM_TAG_KEY = 'c2pp:cluster';

// Exact-match keys, lower-cased for comparison.
const K8S_NODE_TAG_KEYS = new Set([
  'eks:cluster-name',
  'aws:eks:cluster-name',
  'eks:nodegroup-name',
  'aks-managed-cluster-name',
  'aks-managed-poolname',
  'aks-managed-clusterrg',
]);
const K8S_NODE_TAG_PREFIXES = ['kubernetes.io/cluster/', 'k8s.io/cluster-autoscaler/'];

// Case-insensitive substrings of a resource / Name tag that name the product.
const PLATFORM_NAME_HINTS = ['c2pp', 'c2-privacy', 'privacy-platform', 'c2_privacy', 'privacy_platform'];

const entriesOf = (tags) =>
  tags && typeof tags === 'object' && !Array.isArray(tags) ? Object.entries(tags) : [];

const lower = (v) => String(v === null || v === undefined ? '' : v).toLowerCase();

const clusterFromK8sTags = (entries) => {
  for (const [key, value] of entries) {
    const k = lower(key);
    if (k === 'eks:cluster-name' || k === 'aws:eks:cluster-name' || k === 'aks-managed-cluster-name') {
      return value;
    }
    if (k.startsWith('kubernetes.io/cluster/')) return key.slice('kubernetes.io/cluster/'.length);
    if (k.startsWith('k8s.io/cluster-autoscaler/')) {
      const rest = key.slice('k8s.io/cluster-autoscaler/'.length);
      if (rest && rest !== 'enabled') return rest;
    }
  }
  return null;
};

const isK8sNodeTag = (key) => {
  const k = lower(key);
  return K8S_NODE_TAG_KEYS.has(k) || K8S_NODE_TAG_PREFIXES.some((p) => k.startsWith(p));
};

const nameHint = (row, entries) => {
  const nameTag = entries.find(([key]) => lower(key) === 'name');
  const candidates = [row.name, row.identifier, row.database, nameTag ? nameTag[1] : null];
  for (const candidate of candidates) {
    const c = lower(candidate);
    if (!c) continue;
    const hit = PLATFORM_NAME_HINTS.find((h) => c.includes(h));
    if (hit) return { value: String(candidate), hint: hit };
  }
  return null;
};

/**
 * Human-readable reason this row is treated as a platform resource, or null when it is not.
 * Used verbatim as the disabled toggle's tooltip.
 */
const platformResourceReason = (row) => {
  if (!row) return null;
  const entries = entriesOf(row.tags);

  const stamped = entries.find(([key]) => lower(key) === PLATFORM_TAG_KEY);
  if (stamped) {
    const cluster = stamped[1] ? ` (cluster "${stamped[1]}")` : '';
    return `Part of the C2 Privacy Platform deployment${cluster} — lifecycle cannot be managed here`;
  }

  if (entries.some(([key]) => isK8sNodeTag(key))) {
    const cluster = clusterFromK8sTags(entries);
    const where = cluster ? ` "${cluster}"` : '';
    return `Kubernetes node in cluster${where} — lifecycle is managed by the cluster, not here`;
  }

  const hint = nameHint(row, entries);
  if (hint) {
    return `Looks like a C2 Privacy Platform host ("${hint.value}") — lifecycle cannot be managed here`;
  }

  return null;
};

const isPlatformResource = (row) => platformResourceReason(row) !== null;

export { platformResourceReason, isPlatformResource, PLATFORM_TAG_KEY };
